import { useCallback, useMemo } from 'react';

import { buildAllFormValues } from 'helpers/candidate_application/form-page-helpers';
import evaluateFieldConditions from 'helpers/evaluate-field-conditions';
import { useSelector } from 'react-redux';

import { selectCandidateApplication } from 'api/candidate-application-slice';

/**
 * Filters subsections to only those with at least one visible field.
 * A field is visible if:
 *  - Its own conditions pass (or it has no conditions), AND
 *  - If it's an `object` or `id_requirements` wrapper, at least one subField's conditions pass.
 *  - For all other types (including array/dynamic_array), own conditions are enough.
 */
const useVisibleSubsections = subsections => {
  const { candidateFields, application } = useSelector(selectCandidateApplication);
  const { fieldPathIndex } = candidateFields;
  const { id_uploads } = application || {};

  // Use shared helper to avoid divergence
  // Include id_uploads in dependencies so visibility updates when uploads change and requirements are recalculated
  // candidateFields includes idRequirementsMet which is recalculated when id_uploads change
  const allValues = useMemo(
    () =>
      buildAllFormValues({
        candidateFields,
        // no watchValues / currentPagePrefixes here
      }),
    [candidateFields, id_uploads],
  );

  const isFieldVisible = useCallback(
    field => {
      if (!field) return false;

      // Wrapper or none wrapped field must pass its own conditions (true if none)
      const fieldVisible = evaluateFieldConditions(field.conditions, allValues, { fieldPathIndex });
      if (!fieldVisible) return false;

      // `object` and `id_requirements` wrappers require at least one visible subField
      if (field?.type === 'object' || field?.type === 'id_requirements') {
        const subFields = Array.isArray(field?.subFields) ? field.subFields : [];
        if (subFields.length === 0) return false;

        const anySubVisible = subFields.some(subField =>
          evaluateFieldConditions(subField?.conditions, allValues, {
            parentCode: field?.code,
            fieldPathIndex,
          }),
        );

        return anySubVisible;
      }

      // For all other types, own conditions are enough
      return true;
    },
    [allValues, fieldPathIndex],
  );

  const visibleSubsections = useMemo(() => {
    if (!Array.isArray(subsections) || subsections.length === 0) return [];

    return subsections.filter(subsection => {
      const fields = Array.isArray(subsection?.fields) ? subsection.fields : [];
      if (fields.length === 0) return false;

      return fields.some(isFieldVisible);
    });
  }, [subsections, isFieldVisible]);

  return visibleSubsections;
};

export default useVisibleSubsections;
